import { Config } from 'core/common/contexts';
import { Platform } from 'core/common/entities';
import { CookiesService } from 'core/common/services/cookie';
import { isValidUuid } from 'core/common/utils/string';
import { AppEnvironment } from './AppEnvironment';

export class AppClientEnvironment implements AppEnvironment {
  private cookiesService: CookiesService;

  private config: Config;

  constructor(cookiesService: CookiesService, config: Config) {
    this.cookiesService = cookiesService;
    this.config = config;
  }

  isTestingEnv() {
    return this.isTestingTokenPresent();
  }

  private isTestingTokenPresent() {
    const token = this.cookiesService.get(this.config.testingTokenCookieKey) || '';

    return isValidUuid(token);
  }

  getSplitId() {
    return this.cookiesService.get('split_id') || '';
  }

  isSafari() {
    const userAgent = this.getUserAgent();

    return !!userAgent.match(/Version\/[\d\\.]+.*Safari/);
  }

  isAndroid() {
    const userAgent = this.getUserAgent();

    return userAgent.toLowerCase().includes('android');
  }

  isAppleMobile() {
    return (
      (/iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !(window as any).MSStream
    );
  }

  isInAppBrowser() {
    const userAgent = this.getUserAgent();

    const inAppBrowsers = [
      'instagram',
      'fban',
      'fbav',
      'twitter',
      'pinterest',
      'linkedin',
      'snapchat',
      'tiktok',
      'wechat',
      'whatsapp',
      'viber',
      'telegram',
      'reddit',
    ];

    return inAppBrowsers.some((app) => userAgent.toLowerCase().includes(app));
  }

  isApple() {
    const userAgent = this.getUserAgent();

    return userAgent.search(/OS X|iPhone|iPad|iOS/i) !== -1;
  }

  isApplePaySupported() {
    return this.isSafari() || this.isAppleMobile();
  }

  isMobileDevice() {
    const userAgent = this.getUserAgent();

    const mobileKeywords = [
      'Android',
      'webOS',
      'iPhone',
      'iPad',
      'iPod',
      'BlackBerry',
      'Windows Phone',
      'IEMobile',
      'Opera Mini',
    ];

    return mobileKeywords.some((keyword) => userAgent.includes(keyword));
  }

  getPlatform(): Platform {
    if (this.isAndroid()) return Platform.ANDROID;

    if (this.isAppleMobile()) return Platform.IOS;

    return Platform.UNKNOWN;
  }

  getUserAgent() {
    return navigator.userAgent;
  }

  getDeviceLanguage() {
    const [deviceLanguage] = navigator.language.split('-');

    return deviceLanguage;
  }

  getProjectName() {
    return this.config.appName;
  }
}
